import { DEFAULT_DEPTH_OF_FIELD } from '../../../renderer/src/depth-of-field';
import { emptyCinematicSequence } from '../../../renderer/src/cinematic-sequence';
import { createVideoPresentation } from '../../../renderer/src/video-presentation';
import { defaultTrackAppearance } from '../../../renderer/src/track-appearance';
import { calculateRideMetrics, type Ride } from '../domain/ride';
import { PLAYBACK_PACE_OPTIONS, playbackDurationSeconds } from '../domain/playback-pace';
import type { VideoJob } from '../services/local-video';
import { appearanceKey, defaultStudioDraft, studioDraftSchema, type StudioDraft } from './studio-draft';

/** Rebuilds the submitted composition without applying another ride's browser preferences. */
export function videoConfigurationDraft(ride: Ride, job: VideoJob): StudioDraft {
  const defaults = defaultStudioDraft(ride, '');
  const timeline = job.timelineDurationSeconds ?? job.durationSeconds;
  const kilometers = calculateRideMetrics(ride.sourcePoints ?? ride.points).distanceKilometers;
  const pace = PLAYBACK_PACE_OPTIONS.find(value => playbackDurationSeconds(kilometers, value) === timeline);
  const preset = studioDraftSchema.shape.preset.parse(job.preset);
  const presentation = job.presentation ?? createVideoPresentation('none');
  const appearance = { preset, presentation, chosen: true };
  return studioDraftSchema.parse({ ...defaults, title: job.title, preset,
    cameraGimbal: job.cameraGimbal ?? 'glide',
    cameraStyleVersion: job.cameraStyleVersion ?? 3, camera: job.camera, distance: job.distance, cameraAngle: job.cameraAngle ?? null,
    line: job.line, mapStyle: job.mapStyle, trackAppearance: job.trackAppearance ?? defaultTrackAppearance(job.line, job.camera, job.mapStyle),
    terrainExaggeration: job.terrainExaggeration ?? 1, hidePlaces: job.hidePlaces ?? false,
    unitSystem: job.unitSystem ?? 'metric', depthOfField: job.depthOfField ?? { ...DEFAULT_DEPTH_OF_FIELD },
    placeTitle: job.placeTitle ? { ...job.placeTitle, enabled: true, lookup: 'custom' }
      : { enabled: false, name: '', region: '' },
    cinematicEnabled: !!(job.cinematicSequence?.waypoints.length || job.cinematicSequence?.cameraTracks),
    cinematicSequence: job.cinematicSequence ?? emptyCinematicSequence(),
    timing: { mode: !job.exportRange?.durationSeconds && pace ? 'pace' : 'duration', pace: pace ?? 3, targetSeconds: timeline },
    exportRange: job.exportRange ?? null,
    keepSourcePace: !!job.exportRange && job.exportRange.durationSeconds === undefined,
    appearances: { ...defaults.appearances, [appearanceKey(preset, false)]: appearance, [appearanceKey(preset, true)]: appearance },
    progress: 0,
  });
}
